// Particle liquid for water and honey: the prediction-relaxation scheme of
// Clavet, Beaudoin & Poulin, "Particle-based Viscoelastic Fluid Simulation"
// (SCA 2005). Gravity and viscosity impulses change velocities; positions are
// predicted, then double density relaxation (density plus near-density) pushes
// neighbours apart or together, which gives incompressibility and a surface
// tension-like cohesion. Velocities are recovered from the moved positions.
// Neighbours come from a spatial hash (Teschner et al. 2003) rebuilt each step.
const P1=73856093,P2=19349663,P3=83492791;

export class ParticleFluid{
  // stiffness and nearStiffness: push per neighbour, as a fraction of the radius,
  // for a density excess equal to the rest density. They are per step, so keep dt fixed.
  // stretchViscosity: also damp pairs that move apart, so a thick thread thins
  // instead of breaking into beads (Clavet et al. damp approaching pairs only).
  constructor({capacity,radius,stiffness=.02,nearStiffness=.06,viscosity=.2,viscosityQuadratic=0,stretchViscosity=false,gravity=-9,collide=null,restDensity=null}){
    this.capacity=capacity;this.h=radius;this.count=0;
    this.x=new Float32Array(capacity*3);this.v=new Float32Array(capacity*3);this.prev=new Float32Array(capacity*3);this.age=new Float32Array(capacity);
    this.stiffness=stiffness;this.nearStiffness=nearStiffness;this.sigma=viscosity;this.beta=viscosityQuadratic;this.stretch=stretchViscosity;this.gravity=gravity;this.collide=collide;
    this.tableSize=1<<Math.ceil(Math.log2(Math.max(64,capacity*2)));
    this.cellStart=new Int32Array(this.tableSize+1);this.cellEntries=new Int32Array(capacity);this.cellOf=new Int32Array(capacity);
    this.neighbours=new Int32Array(capacity*48);this.neighbourCount=new Int32Array(capacity);
    // Rest density of a particle inside a lattice at half the radius spacing.
    this.restDensity=restDensity??ParticleFluid.latticeDensity(radius,radius*.5);
  }
  static latticeDensity(h,spacing){
    let rho=0;const n=Math.ceil(h/spacing);
    for(let x=-n;x<=n;x++)for(let y=-n;y<=n;y++)for(let z=-n;z<=n;z++){
      if(!x&&!y&&!z)continue;const q=Math.hypot(x,y,z)*spacing/h;if(q<1)rho+=(1-q)**2;
    }
    return rho;
  }
  get spacing(){return this.h*.5;}
  add(x,y,z,vx=0,vy=0,vz=0){
    if(this.count>=this.capacity)return -1;
    const i=this.count++,k=i*3;
    this.x[k]=x;this.x[k+1]=y;this.x[k+2]=z;this.v[k]=vx;this.v[k+1]=vy;this.v[k+2]=vz;this.prev[k]=x;this.prev[k+1]=y;this.prev[k+2]=z;this.age[i]=0;
    return i;
  }
  // Remove particle i by moving the last one into its slot.
  remove(i){
    const last=--this.count;if(i===last)return;
    for(let a=0;a<3;a++){this.x[i*3+a]=this.x[last*3+a];this.v[i*3+a]=this.v[last*3+a];this.prev[i*3+a]=this.prev[last*3+a];}
    this.age[i]=this.age[last];
  }
  clear(){this.count=0;}
  hash(i){
    const h=this.h,k=i*3;
    return ((Math.floor(this.x[k]/h)*P1)^(Math.floor(this.x[k+1]/h)*P2)^(Math.floor(this.x[k+2]/h)*P3))&(this.tableSize-1);
  }
  // Counting sort by hash bucket, then gather neighbours within h from 27 cells.
  buildNeighbours(){
    const n=this.count,start=this.cellStart,entries=this.cellEntries,cellOf=this.cellOf,h=this.h,h2=h*h,x=this.x,T=this.tableSize-1;
    start.fill(0);
    for(let i=0;i<n;i++){const c=this.hash(i);cellOf[i]=c;start[c+1]++;}
    for(let c=0;c<this.tableSize;c++)start[c+1]+=start[c];
    const fill=start.slice(0,this.tableSize);
    for(let i=0;i<n;i++)entries[fill[cellOf[i]]++]=i;
    const list=this.neighbours,counts=this.neighbourCount;
    for(let i=0;i<n;i++){
      const k=i*3,cx=Math.floor(x[k]/h),cy=Math.floor(x[k+1]/h),cz=Math.floor(x[k+2]/h);let m=0;
      for(let dz=-1;dz<=1;dz++)for(let dy=-1;dy<=1;dy++)for(let dx=-1;dx<=1;dx++){
        const c=(((cx+dx)*P1)^((cy+dy)*P2)^((cz+dz)*P3))&T;
        for(let e=start[c];e<start[c+1];e++){
          const j=entries[e];if(j<=i)continue;
          const rx=x[j*3]-x[k],ry=x[j*3+1]-x[k+1],rz=x[j*3+2]-x[k+2];
          if(rx*rx+ry*ry+rz*rz<h2&&m<48)list[i*48+m++]=j;
        }
      }
      counts[i]=m;
    }
  }
  step(dt){
    const n=this.count;if(!n||!(dt>0))return;
    const x=this.x,v=this.v,prev=this.prev,h=this.h,list=this.neighbours,counts=this.neighbourCount;
    for(let i=0;i<n;i++){v[i*3+1]+=this.gravity*dt;this.age[i]+=dt;}
    this.buildNeighbours();
    // Viscosity impulses along each approaching pair.
    if(this.sigma||this.beta)for(let i=0;i<n;i++)for(let m=0;m<counts[i];m++){
      const j=list[i*48+m],a=i*3,b=j*3;
      let rx=x[b]-x[a],ry=x[b+1]-x[a+1],rz=x[b+2]-x[a+2];const r=Math.hypot(rx,ry,rz);if(r<1e-9)continue;rx/=r;ry/=r;rz/=r;
      const q=r/h,u=(v[a]-v[b])*rx+(v[a+1]-v[b+1])*ry+(v[a+2]-v[b+2])*rz;
      if(u<=0&&!this.stretch)continue;
      // Never more than halts the pair, so large viscosities stay stable.
      const I=Math.sign(u)*Math.min(Math.abs(u)*.5,dt*(1-q)*(this.sigma*Math.abs(u)+this.beta*u*u)*.5);
      v[a]-=I*rx;v[a+1]-=I*ry;v[a+2]-=I*rz;v[b]+=I*rx;v[b+1]+=I*ry;v[b+2]+=I*rz;
    }
    for(let k=0;k<n*3;k++){prev[k]=x[k];x[k]+=v[k]*dt;}
    this.buildNeighbours();
    // Double density relaxation. Pressure is in units of displacement per step.
    const rho0=this.restDensity,k0=this.stiffness*h/rho0,kn=this.nearStiffness*h/rho0;
    const rho=this.rho??=new Float32Array(this.capacity),near=this.near??=new Float32Array(this.capacity);
    rho.fill(0,0,n);near.fill(0,0,n);
    for(let i=0;i<n;i++)for(let m=0;m<counts[i];m++){
      const j=list[i*48+m],a=i*3,b=j*3,r=Math.hypot(x[b]-x[a],x[b+1]-x[a+1],x[b+2]-x[a+2]),q=1-r/h;
      if(q<=0)continue;const q2=q*q,q3=q2*q;rho[i]+=q2;rho[j]+=q2;near[i]+=q3;near[j]+=q3;
    }
    for(let i=0;i<n;i++){
      const P=k0*(rho[i]-rho0),Pn=kn*near[i],a=i*3;
      for(let m=0;m<counts[i];m++){
        const j=list[i*48+m],b=j*3;
        let rx=x[b]-x[a],ry=x[b+1]-x[a+1],rz=x[b+2]-x[a+2];const r=Math.hypot(rx,ry,rz);if(r<1e-9)continue;
        const q=1-r/h;if(q<=0)continue;
        const D=(P*q+Pn*q*q)*.5/r;rx*=D;ry*=D;rz*=D;
        x[b]+=rx;x[b+1]+=ry;x[b+2]+=rz;x[a]-=rx;x[a+1]-=ry;x[a+2]-=rz;
      }
    }
    if(this.collide)for(let i=0;i<n;i++)this.collide(x,v,prev,i,dt);
    const inv=1/dt;for(let k=0;k<n*3;k++)v[k]=(x[k]-prev[k])*inv;
  }
}
